import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createOfferApi,
  getOffersApi,
  updateOfferApi,
  deleteOfferApi,
  toggleOfferApi,
} from "../../api/offersApi";

const initialState = {
  offers: [],
  loading: false,
  error: null,
};

// GET OFFERS
export const fetchOffers = createAsyncThunk(
  "offers/fetchOffers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getOffersApi();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// CREATE OFFER
// ✅ FIX: Don't send 'restaurant' in payload — backend uses req.user._id
export const createOffer = createAsyncThunk(
  "offers/createOffer",
  async (payload, { rejectWithValue }) => {
    try {
      // ✅ Strip restaurant field if accidentally included
      const { restaurant, ...safePayload } = payload;
      const { data } = await createOfferApi(safePayload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// UPDATE OFFER
export const updateOffer = createAsyncThunk(
  "offers/updateOffer",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await updateOfferApi(id, formData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// DELETE OFFER
export const deleteOffer = createAsyncThunk(
  "offers/deleteOffer",
  async (id, { rejectWithValue }) => {
    try {
      await deleteOfferApi(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// TOGGLE OFFER
export const toggleOffer = createAsyncThunk(
  "offers/toggleOffer",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await toggleOfferApi(id);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const offerSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    clearOfferError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOffers.pending,    (s) => { s.loading = true;  s.error = null; })
      .addCase(fetchOffers.fulfilled,  (s, a) => { s.loading = false; s.offers = a.payload || []; })
      .addCase(fetchOffers.rejected,   (s, a) => { s.loading = false; s.error = a.payload; })

      .addCase(createOffer.pending,    (s) => { s.loading = true;  s.error = null; })
      .addCase(createOffer.fulfilled,  (s, a) => { s.loading = false; if (a.payload) s.offers.unshift(a.payload); })
      .addCase(createOffer.rejected,   (s, a) => { s.loading = false; s.error = a.payload; })

      .addCase(updateOffer.pending,    (s) => { s.loading = true;  s.error = null; })
      .addCase(updateOffer.fulfilled,  (s, a) => {
        s.loading = false;
        const idx = s.offers.findIndex((o) => o._id === a.payload?._id);
        if (idx !== -1) s.offers[idx] = a.payload;
      })
      .addCase(updateOffer.rejected,   (s, a) => { s.loading = false; s.error = a.payload; })

      .addCase(deleteOffer.fulfilled,  (s, a) => { s.offers = s.offers.filter((o) => o._id !== a.payload); })
      .addCase(deleteOffer.rejected,   (s, a) => { s.error = a.payload; })

      .addCase(toggleOffer.fulfilled,  (s, a) => {
        const idx = s.offers.findIndex((o) => o._id === a.payload?._id);
        if (idx !== -1) s.offers[idx] = a.payload;
      })
      .addCase(toggleOffer.rejected,   (s, a) => { s.error = a.payload; });
  },
});

export const { clearOfferError } = offerSlice.actions;

// Selectors
export const selectOffers        = (s) => s.offers.offers;
export const selectOffersLoading = (s) => s.offers.loading;
export const selectOffersError   = (s) => s.offers.error;

export default offerSlice.reducer;